import { formatSEK } from '@budgie/shared';
import { useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { trpc } from '../../lib/trpc';
import { TableSkeleton } from '../common/Skeleton';

type Props = {
  open: boolean;
  onClose: () => void;
  pattern: string;
  matchType: 'contains' | 'exact' | 'starts_with';
  categoryId: string;
  categoryName: string;
  existingRuleId?: string | undefined;
  /** Whether this is a new rule (create) or editing an existing one */
  mode: 'create' | 'edit';
};

export function RulePreviewDialog({
  open,
  onClose,
  pattern,
  matchType,
  categoryId,
  categoryName,
  existingRuleId,
  mode,
}: Props) {
  const [includeManualOverrides, setIncludeManualOverrides] = useState(false);
  const utils = trpc.useUtils();

  const previewQuery = trpc.categoryRule.previewImpact.useQuery(
    { pattern, matchType, categoryId, existingRuleId },
    { enabled: open && pattern.length > 0, retry: false },
  );

  const applyMutation = trpc.categoryRule.applyRecategorization.useMutation({
    onSuccess: async (data) => {
      await Promise.all([
        utils.categoryRule.list.invalidate(),
        utils.categoryRule.findDuplicateGroups.invalidate(),
        utils.insights.overview.invalidate(),
        utils.insights.categoryTransactions.invalidate(),
        utils.transaction.list.invalidate(),
      ]);
      toast.success(
        data.updatedCount > 0
          ? `${data.updatedCount} transaktion${data.updatedCount === 1 ? '' : 'er'} uppdaterad${data.updatedCount === 1 ? '' : 'e'}`
          : 'Regel sparad (inga transaktioner ändrades)',
      );
      onClose();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const preview = previewQuery.data;
  const summary = preview?.summary;
  const transactions = preview?.affectedTransactions ?? [];

  const visibleTransactions = includeManualOverrides
    ? transactions
    : transactions.filter((t) => !t.isManualOverride);

  const handleApply = () => {
    applyMutation.mutate({
      pattern,
      matchType,
      categoryId,
      includeManualOverrides,
      saveRule: true,
      existingRuleId,
    });
  };

  const handleSaveRuleOnly = () => {
    applyMutation.mutate({
      pattern,
      matchType,
      categoryId,
      includeManualOverrides: false,
      saveRule: true,
      existingRuleId,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Förhandsgranska ny regel' : 'Förhandsgranska regeländring'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
            <span>
              Mönster:{' '}
              <code className="rounded-md bg-muted px-1.5 py-0.5 font-mono text-foreground">
                {pattern}
              </code>
            </span>
            <span>→</span>
            <span className="font-medium text-foreground">{categoryName}</span>
          </div>

          {previewQuery.isLoading && <TableSkeleton rows={3} columns={5} />}

          {previewQuery.error && (
            <p className="text-sm text-negative">{previewQuery.error.message}</p>
          )}

          {summary && (
            <div className="flex flex-wrap gap-3 text-xs text-muted-foreground tabular-nums">
              <span>
                Totalt matchande:{' '}
                <strong className="text-foreground">{summary.totalMatching}</strong>
              </span>
              <span>
                Kommer ändras:{' '}
                <strong className="text-foreground">{summary.willRecategorize}</strong>
              </span>
              {summary.manualOverrideCount > 0 && (
                <span>
                  Manuella: <strong className="text-warning">{summary.manualOverrideCount}</strong>
                </span>
              )}
              {summary.alreadyCorrect > 0 && (
                <span>
                  Redan rätt: <strong className="text-positive">{summary.alreadyCorrect}</strong>
                </span>
              )}
              {summary.conflictCount > 0 && (
                <span>
                  Konflikter: <strong className="text-negative">{summary.conflictCount}</strong>
                </span>
              )}
            </div>
          )}

          {summary && summary.totalMatching === 0 && (
            <div className="rounded-xl bg-muted/60 p-4 text-center">
              <p className="text-sm text-muted-foreground">
                Inga transaktioner påverkas av denna regel
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Du kan fortfarande spara regeln för framtida transaktioner.
              </p>
            </div>
          )}

          {summary && summary.conflictCount > 0 && (
            <div className="rounded-xl border border-warning/25 bg-warning-soft px-3 py-2 text-xs text-warning">
              {summary.conflictCount} transaktion
              {summary.conflictCount === 1 ? '' : 'er'} matchar en annan regel med högre säkerhet.
              Dessa kan komma att omklassificeras igen vid framtida regelkörningar.
            </div>
          )}

          {visibleTransactions.length > 0 && (
            <div className="max-h-64 overflow-x-auto overflow-y-auto rounded-xl border border-border">
              <Table className="min-w-[600px]">
                <TableHeader>
                  <TableRow>
                    <TableHead className="whitespace-nowrap">Datum</TableHead>
                    <TableHead>Beskrivning</TableHead>
                    <TableHead className="whitespace-nowrap">Nuvarande</TableHead>
                    <TableHead className="whitespace-nowrap">Ny</TableHead>
                    <TableHead className="whitespace-nowrap text-right">Belopp</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleTransactions.map((t) => (
                    <TableRow key={t.id}>
                      <TableCell className="whitespace-nowrap font-mono text-xs text-muted-foreground tabular-nums">
                        {t.date}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <span className="max-w-[250px] truncate text-sm">{t.description}</span>
                          {t.isManualOverride && (
                            <Badge
                              variant="outline"
                              className="shrink-0 border-transparent bg-warning-soft text-[10px] text-warning"
                            >
                              Manuell
                            </Badge>
                          )}
                          {t.conflictingRule && (
                            <Badge
                              variant="outline"
                              className="shrink-0 border-transparent bg-negative-soft text-[10px] text-negative"
                            >
                              Konflikt
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                        {t.currentCategoryName ?? 'Ingen'}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-xs font-medium text-foreground">
                        {t.proposedCategoryName}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-right font-mono text-xs tabular-nums">
                        {formatSEK(t.amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {summary && summary.manualOverrideCount > 0 && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Checkbox
                id="include-manual-overrides"
                checked={includeManualOverrides}
                onCheckedChange={(checked) => setIncludeManualOverrides(checked === true)}
              />
              <label htmlFor="include-manual-overrides">
                Inkludera manuellt ändrade transaktioner ({summary.manualOverrideCount} st)
              </label>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Avbryt
          </Button>
          {summary && summary.totalMatching === 0 ? (
            <Button type="button" onClick={handleSaveRuleOnly} disabled={applyMutation.isPending}>
              {applyMutation.isPending ? 'Sparar…' : 'Spara regel ändå'}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleApply}
              disabled={applyMutation.isPending || previewQuery.isLoading}
            >
              {applyMutation.isPending
                ? 'Tillämpar…'
                : `Tillämpa (${includeManualOverrides ? (summary?.totalMatching ?? 0) : (summary?.willRecategorize ?? 0)} st)`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
